import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { getClubCoaches, getClubSports, type ClubCoach, type ClubSport } from "../../services/api";
import { createInvitation } from "../../services/invitations";
import ClubAdminLayout from "../../layouts/ClubAdminLayout";
import { ClubNavIcon } from "../../layouts/clubNav";
import AdminPageHeader, { AdminButton } from "../../components/admin/AdminPageHeader";
import { AdminCard, StatCard, StatGrid } from "../../components/admin/AdminCard";
import PendingInvitations from "../../components/club/PendingInvitations";
import LoginAsButton from "../../components/LoginAsButton";
import toast from "react-hot-toast";
import { formatDate } from "../../lib/dateFormat";

/**
 * Antrenorii clubului.
 *
 * Built like the athletes roster: four figures that are worth acting on, then
 * one card holding the search, the filters and the table. The figures are not
 * decoration — each one is a thing a club admin can fix. "Neconfirmați" and
 * "Fără echipă" in particular: a coach in either state cannot do the job they
 * were hired for, and nothing else on this page would have told you.
 */
export default function ClubCoaches() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { activeClubId } = useAuth();

  const [coaches, setCoaches] = useState<ClubCoach[]>([]);
  const [sports, setSports] = useState<ClubSport[]>([]);
  const [loading, setLoading] = useState(true);

  const [showInviteModal, setShowInviteModal] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteSportId, setInviteSportId] = useState("");
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteReloadKey, setInviteReloadKey] = useState(0);

  const [search, setSearch] = useState("");
  const [teamFilter, setTeamFilter] = useState("");
  const [sportFilter, setSportFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");

  const load = useCallback(async () => {
    if (!activeClubId) return;
    setLoading(true);
    try {
      const [coachesData, sportsData] = await Promise.all([
        getClubCoaches(activeClubId),
        getClubSports(activeClubId).catch(() => [] as ClubSport[]),
      ]);
      setCoaches(coachesData);
      setSports(sportsData);
    } catch (error) {
      console.error("Failed to load coaches:", error);
    }
    setLoading(false);
  }, [activeClubId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInviteCoach = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!activeClubId || !inviteEmail) return;

    setInviteLoading(true);
    try {
      await createInvitation({
        email: inviteEmail.trim(),
        role_code: "coach",
        club_id: activeClubId,
        ...(inviteSportId ? { sport_id: inviteSportId } : {}),
      });
      toast.success(t("invite.sent", "Invitație trimisă cu succes!"));
      setInviteEmail("");
      setInviteSportId("");
      setShowInviteModal(false);
      setInviteReloadKey((k) => k + 1);
    } catch (error: any) {
      toast.error(error.response?.data?.error || t("invite.error", "Nu s-a putut trimite invitația"));
    }
    setInviteLoading(false);
  };

  /** Teams across every coach → filter options, with no extra request. */
  const teamOptions = useMemo(
    () =>
      Array.from(new Map(coaches.flatMap((c) => c.teams).map((tm) => [tm.id, tm])).values()).sort(
        (a, b) => a.name.localeCompare(b.name, "ro")
      ),
    [coaches]
  );

  const stats = useMemo(() => {
    const unconfirmed = coaches.filter((c) => c.user.email_verified === false).length;
    const withoutTeam = coaches.filter((c) => c.teams.length === 0).length;
    const inactive = coaches.filter((c) => c.user.status === "inactive").length;
    const teamsCovered = new Set(coaches.flatMap((c) => c.teams.map((tm) => tm.id))).size;
    return { unconfirmed, withoutTeam, inactive, teamsCovered };
  }, [coaches]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return coaches.filter((c) => {
      if (q) {
        const haystack = [c.user.full_name, c.user.email, ...(c.sports || []).map((s) => s.name)]
          .filter(Boolean)
          .join(" ")
          .toLowerCase();
        if (!haystack.includes(q)) return false;
      }
      if (teamFilter && !c.teams.some((tm) => tm.id === teamFilter)) return false;
      if (sportFilter && !(c.sports || []).some((s) => s.id === sportFilter)) return false;
      if (statusFilter === "unconfirmed" && c.user.email_verified !== false) return false;
      if (statusFilter === "no_team" && c.teams.length > 0) return false;
      if (statusFilter === "inactive" && c.user.status !== "inactive") return false;
      if (statusFilter === "active" && (c.user.status === "inactive" || c.user.email_verified === false))
        return false;
      return true;
    });
  }, [coaches, search, teamFilter, sportFilter, statusFilter]);

  const hasFilters = !!(search || teamFilter || sportFilter || statusFilter);
  const clearFilters = () => {
    setSearch("");
    setTeamFilter("");
    setSportFilter("");
    setStatusFilter("");
  };

  const selectCls =
    "px-3 py-2 text-sm border rounded-lg bg-slate-50 border-slate-200 text-slate-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 min-w-0";

  const statusChip = (coach: ClubCoach) => {
    const status = coach.user.status || "active";
    // An account nobody has ever claimed is not "Activ". Shown instead of the
    // status rather than beside it: until they confirm, that is the thing to
    // act on. A suspended account still wins — that is a decision the club
    // made and has to keep seeing.
    if (coach.user.email_verified === false && status !== "inactive") {
      return (
        <span
          className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full bg-amber-50 text-amber-700 border-amber-200"
          title={t(
            "admin.coachUnconfirmedHint",
            "Nu și-a confirmat adresa de email, deci nu a intrat niciodată în cont."
          )}
        >
          <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
          {t("common.statusUnconfirmed", "Neconfirmat")}
        </span>
      );
    }
    const cfg =
      {
        active: { dot: "bg-emerald-500", chip: "bg-emerald-50 text-emerald-700 border-emerald-200", label: t("common.statusActive", "Activ") },
        demo: { dot: "bg-blue-500", chip: "bg-blue-50 text-blue-700 border-blue-200", label: t("common.statusDemo", "Demo") },
        inactive: { dot: "bg-red-500", chip: "bg-red-50 text-red-700 border-red-200", label: t("common.statusInactive", "Inactiv") },
      }[status] || { dot: "bg-slate-400", chip: "bg-slate-50 text-slate-600 border-slate-200", label: status };
    return (
      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium border rounded-full ${cfg.chip}`}>
        <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
        {cfg.label}
      </span>
    );
  };

  if (loading) {
    return (
      <ClubAdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-8 h-8 border-4 border-indigo-600 rounded-full animate-spin border-t-transparent" />
        </div>
      </ClubAdminLayout>
    );
  }

  return (
    <ClubAdminLayout>
      <div className="mx-auto max-w-[1500px] space-y-6">
        <AdminPageHeader
          title={t("admin.coaches", "Antrenori")}
          subtitle={t("clubCoaches.subtitle", "Cine antrenează, pe ce sport și cu câți sportivi.")}
          icon={<ClubNavIcon id="coaches" className="w-6 h-6" />}
          actions={
            <AdminButton
              onClick={() => setShowInviteModal(true)}
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              }
            >
              {t("invite.inviteCoach", "Invită antrenor")}
            </AdminButton>
          }
        />

        <StatGrid>
          <StatCard
            label={t("admin.coaches", "Antrenori")}
            value={coaches.length}
            dotClass="bg-indigo-500"
            hint={`${stats.teamsCovered} ${t("admin.teams", "echipe").toLowerCase()} acoperite`}
            hintTone="muted"
          />
          <StatCard
            label={t("clubCoaches.unconfirmed", "Neconfirmați")}
            value={stats.unconfirmed}
            dotClass="bg-amber-500"
            valueClass={stats.unconfirmed > 0 ? "text-amber-600" : "text-slate-800"}
            hint={
              stats.unconfirmed > 0
                ? t("clubCoaches.unconfirmedHint", "nu au intrat niciodată în cont")
                : t("clubCoaches.allConfirmed", "toți au intrat în cont")
            }
            hintTone={stats.unconfirmed > 0 ? "down" : "up"}
          />
          <StatCard
            label={t("clubCoaches.withoutTeam", "Fără echipă")}
            value={stats.withoutTeam}
            dotClass="bg-rose-500"
            valueClass={stats.withoutTeam > 0 ? "text-rose-600" : "text-slate-800"}
            hint={
              stats.withoutTeam > 0
                ? t("clubCoaches.withoutTeamHint", "nu pot ține prezența")
                : t("clubCoaches.allAssigned", "toți au echipă")
            }
            hintTone={stats.withoutTeam > 0 ? "down" : "up"}
          />
          <StatCard
            label={t("clubCoaches.inactive", "Inactivi")}
            value={stats.inactive}
            dotClass="bg-slate-400"
            hint={t("clubCoaches.inactiveHint", "conturi suspendate")}
            hintTone="muted"
          />
        </StatGrid>

        {/* Everyone the club is still waiting on — whoever sent the invitation.
            Activating one here skips the email entirely. */}
        <PendingInvitations
          clubId={activeClubId}
          roles={["coach"]}
          reloadKey={inviteReloadKey}
          onChanged={load}
        />

        <AdminCard>
          {/* Toolbar */}
          <div className="flex flex-col gap-2 p-3 border-b border-slate-100 sm:flex-row sm:items-center">
            <div className="relative flex-1 min-w-0">
              <svg className="absolute w-4 h-4 -translate-y-1/2 left-3 top-1/2 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <input
                type="text"
                placeholder={t("clubCoaches.search_placeholder", "Caută după nume, email sau sport...")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full py-2 pl-9 pr-3 text-sm border rounded-lg bg-slate-50 border-slate-200 text-slate-800 placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
            </div>
            <div className="grid grid-cols-2 gap-2 sm:flex sm:items-center sm:gap-2">
              {sports.length > 0 && (
                <select value={sportFilter} onChange={(e) => setSportFilter(e.target.value)} className={selectCls}>
                  <option value="">{t("clubCoaches.allSports", "Toate sporturile")}</option>
                  {sports.map((sp) => (
                    <option key={sp.id} value={sp.id}>{sp.name}</option>
                  ))}
                </select>
              )}
              {teamOptions.length > 0 && (
                <select value={teamFilter} onChange={(e) => setTeamFilter(e.target.value)} className={selectCls}>
                  <option value="">{t("clubAthletes.all_teams", "Toate echipele")}</option>
                  {teamOptions.map((tm) => (
                    <option key={tm.id} value={tm.id}>{tm.name}</option>
                  ))}
                </select>
              )}
              <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={selectCls}>
                <option value="">{t("clubCoaches.allStatuses", "Toate statusurile")}</option>
                <option value="active">{t("common.statusActive", "Activ")}</option>
                <option value="unconfirmed">{t("common.statusUnconfirmed", "Neconfirmat")}</option>
                <option value="no_team">{t("clubCoaches.withoutTeam", "Fără echipă")}</option>
                <option value="inactive">{t("common.statusInactive", "Inactiv")}</option>
              </select>
              {hasFilters && (
                <button
                  onClick={clearFilters}
                  className="px-3 py-2 text-sm font-medium whitespace-nowrap text-slate-500 hover:text-slate-800"
                >
                  {t("common.clearFilters", "Resetează")}
                </button>
              )}
              <span className="px-1 text-sm text-slate-500 whitespace-nowrap">
                {filtered.length}
                {filtered.length !== coaches.length ? ` / ${coaches.length}` : ""}
              </span>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left border-b border-slate-200 bg-slate-50">
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("common.name", "Nume")}</th>
                  <th className="hidden px-4 py-3 font-semibold md:table-cell text-slate-600">{t("common.email", "Email")}</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("common.status", "Status")}</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("clubCoaches.sports", "Sporturi")}</th>
                  <th className="px-4 py-3 font-semibold text-slate-600">{t("admin.teams", "Echipe")}</th>
                  <th className="px-4 py-3 font-semibold text-right text-slate-600">{t("admin.athletes", "Sportivi")}</th>
                  <th className="hidden px-4 py-3 font-semibold sm:table-cell text-slate-600">{t("common.joined", "Înscris")}</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody>
                {filtered.map((coach) => (
                  <tr key={coach.id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60">
                    <td className="px-4 py-3 align-top">
                      <div className="flex items-center gap-3 min-w-0">
                        <div className="flex items-center justify-center flex-shrink-0 text-sm font-bold rounded-full w-9 h-9 bg-amber-100 text-amber-600">
                          {(coach.user.full_name || coach.user.email).substring(0, 2).toUpperCase()}
                        </div>
                        <button
                          onClick={() => navigate(`/admin/users/${coach.user.id}`)}
                          className="min-w-0 font-medium text-left truncate text-slate-800 hover:text-indigo-600"
                        >
                          {coach.user.full_name || coach.user.email}
                        </button>
                      </div>
                    </td>
                    <td className="hidden px-4 py-3 align-top md:table-cell break-all text-slate-600">
                      {coach.user.email}
                    </td>
                    <td className="px-4 py-3 align-top whitespace-nowrap">{statusChip(coach)}</td>
                    <td className="px-4 py-3 align-top">
                      {(coach.sports || []).length === 0 ? (
                        // Worth saying out loud: with no sport, a coordinator
                        // cannot see them and the sport filters skip them.
                        <span className="text-xs font-semibold text-amber-600">
                          {t("clubCoaches.noSport", "Niciun sport")}
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {(coach.sports || []).map((sp) => (
                            <span
                              key={sp.id}
                              className="px-2 py-0.5 text-xs font-medium rounded-full bg-sky-50 text-sky-700 whitespace-nowrap"
                            >
                              {sp.name}
                            </span>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 align-top">
                      {coach.teams.length === 0 ? (
                        <span className="text-xs font-semibold text-rose-600">
                          {t("clubCoaches.noTeam", "Fără echipă")}
                        </span>
                      ) : (
                        <div className="flex flex-wrap gap-1">
                          {coach.teams.map((team) => (
                            // Straight to the team's 360: from "who coaches
                            // this" to "how is this squad doing" in one tap.
                            <button
                              key={team.id}
                              onClick={() => navigate(`/dashboard/admin-club/teams/${team.id}`)}
                              className="px-2 py-0.5 text-xs rounded-full bg-indigo-100 text-indigo-700 whitespace-nowrap hover:bg-indigo-200"
                              title={`Vezi echipa ${team.name}`}
                            >
                              {team.name}
                            </button>
                          ))}
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right align-top tabular-nums text-slate-700">
                      {coach.athletes_count ?? 0}
                    </td>
                    <td className="hidden px-4 py-3 align-top sm:table-cell text-slate-600 whitespace-nowrap">
                      {coach.joined_at ? formatDate(new Date(coach.joined_at)) : "-"}
                    </td>
                    <td className="px-4 py-3 text-right align-top">
                      <LoginAsButton userId={coach.user.id} compact />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {filtered.length === 0 && (
              <div className="py-12 text-center text-slate-400">
                {coaches.length === 0
                  ? t("admin.noCoaches", "Niciun antrenor încă")
                  : t("common.no_results", "Niciun rezultat")}
              </div>
            )}
          </div>
        </AdminCard>
      </div>

      {/* Invite */}
      {showInviteModal && (
        <div
          className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/50"
          onClick={() => setShowInviteModal(false)}
        >
          <div
            className="w-full max-w-lg bg-white rounded-2xl shadow-xl max-h-[90vh] overflow-hidden flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
              <h3 className="text-lg font-bold text-slate-800">{t("invite.inviteCoach", "Invită antrenor")}</h3>
              <button
                onClick={() => setShowInviteModal(false)}
                className="p-2 -mr-2 rounded-lg text-slate-500 hover:bg-slate-100"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <form onSubmit={handleInviteCoach} className="flex flex-col flex-1 min-h-0">
              <div className="p-5 space-y-4 overflow-y-auto">
                <div>
                  <label className="block mb-2 text-sm font-medium text-slate-700">
                    {t("common.email", "Email")}
                  </label>
                  <input
                    type="email"
                    required
                    value={inviteEmail}
                    onChange={(e) => setInviteEmail(e.target.value)}
                    placeholder="antrenor@exemplu.ro"
                    className="w-full px-3 py-2 text-base border rounded-lg border-slate-300 text-slate-700 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                {sports.length > 0 && (
                  <div>
                    <label className="block mb-2 text-sm font-medium text-slate-700">
                      {t("clubCoaches.sport", "Sport")}
                    </label>
                    <select
                      value={inviteSportId}
                      onChange={(e) => setInviteSportId(e.target.value)}
                      className="w-full px-3 py-2 text-base bg-white border rounded-lg border-slate-300 text-slate-700 lg:text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="">{t("clubCoaches.sportLater", "Îl aleg mai târziu")}</option>
                      {sports.map((sp) => (
                        <option key={sp.id} value={sp.id}>{sp.name}</option>
                      ))}
                    </select>
                    <p className="mt-1 text-[11px] text-slate-400">
                      {t(
                        "clubCoaches.sportHint",
                        "Fără sport, antrenorul nu apare la coordonatorul sportului respectiv."
                      )}
                    </p>
                  </div>
                )}
              </div>
              <div className="flex items-center justify-end gap-3 px-5 py-4 border-t border-slate-100">
                <button
                  type="button"
                  onClick={() => setShowInviteModal(false)}
                  className="px-4 py-2.5 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-xl font-medium text-sm"
                >
                  {t("common.cancel", "Anulează")}
                </button>
                <button
                  type="submit"
                  disabled={inviteLoading}
                  className="px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-medium text-sm disabled:opacity-50"
                >
                  {inviteLoading ? t("common.sending", "Se trimite...") : t("invite.send", "Trimite invitația")}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </ClubAdminLayout>
  );
}
